import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Eye, EyeOff, Trash2, Pencil, Check, X, RefreshCw } from "lucide-react";

const AdminComments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/comments");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (c, status) => {
    try {
      await api.patch(`/admin/comments/${c.id}/status`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm("حذف التعليق؟")) return;
    try {
      await api.delete(`/admin/comments/${c.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const banUser = async (c) => {
    if (!window.confirm(`تجميد العضو ${c.user_name}؟`)) return;
    try {
      await api.patch(`/admin/members/${c.user_id}`, { status: "frozen" });
      toast.success("تم تجميد العضو");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/comments/${editing.id}`, { body: editBody });
      toast.success("تم التعديل");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-comments">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline mb-3">التعليقات</div>
          <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone">إدارة التعليقات</h1>
        </div>
        <button onClick={load} className="btn-ghost"><RefreshCw size={14} /></button>
      </div>

      {loading && <div className="text-white/40 overline animate-pulse">…</div>}
      {!loading && items.length === 0 && <div className="text-white/50 font-arabic">لا توجد تعليقات بعد.</div>}

      <ul className="space-y-4">
        {items.map((c) => (
          <li key={c.id} className="border border-white/10 bg-ink-soft p-6" data-testid={`comment-row-${c.id}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-arabic font-bold text-bone">{c.user_name}</span>
                <span className={`text-xs uppercase tracking-[0.2em] ${c.status === "approved" ? "text-emerald-400" : c.status === "hidden" ? "text-red-400" : "text-amber-400"}`}>
                  {c.status === "approved" ? "ظاهر" : c.status === "hidden" ? "مخفي" : "قيد المراجعة"}
                </span>
                <span className="text-xs text-white/40" dir="ltr">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="flex gap-2">
                {c.status !== "approved" && <button onClick={() => setStatus(c, "approved")} className="text-emerald-400 hover:text-emerald-300" title="إظهار" data-testid={`comment-approve-${c.id}`}><Eye size={14} /></button>}
                {c.status !== "hidden" && <button onClick={() => setStatus(c, "hidden")} className="text-amber-400 hover:text-amber-300" title="إخفاء" data-testid={`comment-hide-${c.id}`}><EyeOff size={14} /></button>}
                <button onClick={() => { setEditing(c); setEditBody(c.body); }} className="text-white/60 hover:text-gold" title="تعديل"><Pencil size={14} /></button>
                <button onClick={() => banUser(c)} className="text-white/60 hover:text-red-400 text-xs">حظر</button>
                <button onClick={() => remove(c)} className="text-white/60 hover:text-red-400" title="حذف"><Trash2 size={14} /></button>
              </div>
            </div>
            {editing?.id === c.id ? (
              <div>
                <textarea rows={4} value={editBody} onChange={(e) => setEditBody(e.target.value)} className="editorial-input resize-none font-arabic" />
                <div className="flex gap-2 mt-3">
                  <button onClick={saveEdit} className="btn-gold py-2 px-4 text-xs"><Check size={14} />حفظ</button>
                  <button onClick={() => setEditing(null)} className="btn-ghost py-2 px-4 text-xs"><X size={14} />إلغاء</button>
                </div>
              </div>
            ) : (
              <p className="text-white/75 whitespace-pre-wrap font-arabic">{c.body}</p>
            )}
            <div className="text-xs text-white/40 mt-3">على المقال: <span className="text-white/55">{c.article_id}</span></div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminComments;
